import { DOCUMENT, Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { startWith, tap } from 'rxjs/operators';

import { AssetCustomField } from '@assetaccountant/api/models/asset-custom-field';
import { SearchAssetsResponse, SearchAssetsResponseItem } from '../../assets/models/search-assets';
import { AppNavigationService } from './app-navigation.service';

export interface SearchAssetsCachedResponse {
  quantity: number;
  pageNumber: number;
  assets: Array<SearchAssetsResponseItem | SearchAssetsInvalidatedItem>;
  depreciationClassifications: string[];
}

export interface SearchAssetsInvalidatedItem {
  id: string;
  name: string;
  code: string | null;
  description: string;
  // WDV may not exist if there is some bad data and we cannot calculate WDV for this asset
  writtenDownValue: 0;
  groupId: number;
  parentGroup?: string;
  groupName: string;
  status: 'Loading';
  customFields?: AssetCustomField[] | null;
  fields?: Record<string, string>;
  quantity?: number;
  quantityUnits?: string;
}

export function toInvalidatedItem(
  asset: SearchAssetsResponseItem | SearchAssetsInvalidatedItem,
): SearchAssetsInvalidatedItem {
  return {
    id: asset.id,
    name: asset.name,
    code: asset.code,
    description: asset.description,
    groupId: asset.groupId,
    parentGroup: asset.parentGroup,
    groupName: asset.groupName,
    // We pop a toast saying there was an error if writtenDownValue is null
    writtenDownValue: 0,
    status: 'Loading',
    fields: asset.fields,
  };
}

@Injectable({
  providedIn: 'root',
})
export class SearchAssetsCache {
  _cache?: {
    registerId: number;
    regimeId: number;
    periodStartDate: string;
    periodEndDate: string;
    response: SearchAssetsCachedResponse;
  };

  constructor(
    private appNavigationService: AppNavigationService,
    @Inject(DOCUMENT) document: Document,
  ) {
    const window = document.defaultView;
    if (window) {
      const cacheItem = window.sessionStorage.getItem('SearchAssetsCache');
      if (cacheItem) {
        this._cache = JSON.parse(cacheItem);
      }
      window.addEventListener('beforeunload', () => {
        if (this._cache) {
          window.sessionStorage.setItem(
            'SearchAssetsCache',
            JSON.stringify({
              ...this._cache,
              response: {
                ...this._cache.response,
                assets: this._cache.response.assets.map(toInvalidatedItem),
              },
            }),
          );
        } else {
          window.sessionStorage.removeItem('SearchAssetsCache');
        }
      });
    }
  }

  get(): SearchAssetsCachedResponse | null {
    if (!this._cache) {
      return null;
    }

    const registerId = this.appNavigationService.getActiveRegisterId();
    const regimeId = this.appNavigationService.getActiveRegimeId();

    if (registerId !== this._cache.registerId || regimeId !== this._cache.regimeId) {
      return null;
    }

    const periodStartDate = this.appNavigationService.getPeriodStartDate();
    const periodEndDate = this.appNavigationService.getPeriodEndDate();

    if (
      periodStartDate !== this._cache.periodStartDate ||
      periodEndDate !== this._cache.periodEndDate
    ) {
      return {
        ...this._cache.response,
        assets: this._cache.response.assets.map(toInvalidatedItem),
      };
    }

    return this._cache.response;
  }

  set(response: SearchAssetsResponse) {
    const registerId = this.appNavigationService.getActiveRegisterId();
    const regimeId = this.appNavigationService.getActiveRegimeId();
    const periodStartDate = this.appNavigationService.getPeriodStartDate();
    const periodEndDate = this.appNavigationService.getPeriodEndDate();

    if (registerId && regimeId && periodStartDate && periodEndDate) {
      this._cache = {
        registerId,
        regimeId,
        periodStartDate,
        periodEndDate,
        response,
      };
    }
  }

  invalidate(assetId: any) {
    if (this._cache) {
      this._cache.response = {
        ...this._cache.response,
        assets: this._cache.response.assets.map(a =>
          a.id === assetId.toString() ? toInvalidatedItem(a) : a,
        ),
      };
    }
  }

  applyCaching(httpResponse: Observable<SearchAssetsResponse>) {
    const cachedResult = this.get();

    const response = httpResponse.pipe(tap(x => this.set(x)));

    return cachedResult ? response.pipe(startWith(cachedResult)) : response;
  }
}
